import {
  DesignNode,
  Revert,
  RevertChange,
} from '../../../../../elements/design/design-node/design-node';
import {
  getConicLength,
  getConicParameter,
  getSegmentConic,
  getSegmentCurve,
  SegmentCurve,
  splitConic,
} from '../../../../../elements/design/design-node/utils/conic';
import {
  getLineSegmentFaces,
  LineSegmentNode,
} from '../../../../../elements/design/design-node/utils/line-segment-node';
import { PointId } from '../../../../../elements/design/design-node/utils/point-node';

export const breakLineSegment = (
  node: DesignNode,
  breakPointId: PointId,
  line: LineSegmentNode,
): RevertChange[] => {
  const changes: RevertChange[] = [];

  const faceIds = getLineSegmentFaces(node, line.id);
  const revertRemoveFaces: Revert<'upsert-face'>[] = [];

  for (let i = faceIds.length - 1; i >= 0; i--) {
    revertRemoveFaces.push(node.removeFace(faceIds[i]));
  }

  changes.unshift(...revertRemoveFaces);
  changes.unshift(node.removeLineSegment(line.id));

  // A curved line is split into two curves that follow the original curve. Both keep the direction
  // of the original line, so that its stripes continue from the first to the second.
  const [start, end] = line.values.map((id) => node.getVertexByPointId(id, 'local'));
  const breakPoint = node.getVertexByPointId(breakPointId, 'local');
  let startCurve: SegmentCurve | undefined;
  let endCurve: SegmentCurve | undefined;
  let splitDistance = start.distanceTo(breakPoint);

  if (line.curve) {
    const conic = getSegmentConic(start, end, line.curve);
    const t = getConicParameter(conic, breakPoint);
    const [first, second] = splitConic(conic, t);
    startCurve = getSegmentCurve(first);
    endCurve = getSegmentCurve(second);
    splitDistance = getConicLength(conic, 0, t);
  }

  changes.unshift(
    node.addLineSegment(line.values[0], breakPointId, line.attributes, startCurve, {
      width: line.width,
      stripe: line.stripe,
    }),
  );
  changes.unshift(
    node.addLineSegment(breakPointId, line.values[1], line.attributes, endCurve, {
      width: line.width,
      stripe: line.stripe && { ...line.stripe, offset: line.stripe.offset + splitDistance },
    }),
  );

  const linePoints = line.values;

  for (const change of revertRemoveFaces) {
    const facePoints = change.node.values!;

    // identify the value that is not contained in the old line
    const keepValue = facePoints.find((x) => !linePoints.includes(x))!;

    // Check if the indices are neighbours
    const neighbours =
      Math.abs(facePoints.indexOf(linePoints[1]) - facePoints.indexOf(linePoints[0])) <= 1;
    // Check if the indices are reversed
    const reversed = facePoints.indexOf(linePoints[0]) > facePoints.indexOf(linePoints[1]);

    /**
     * Create the faces with indices in normal order if any of these scenarios are true:
     * 1. The index order of the face matches with the line, and the indices are neighbours.
     * 2. The order is reversed, but the indices are not neighbours.
     *
     * Otherwise we create the new faces with their indices in reversed order.
     */
    if ((!reversed && neighbours) || (reversed && !neighbours)) {
      changes.unshift(node.addFace(breakPointId, keepValue, linePoints[0], change.node.attributes));
      changes.unshift(node.addFace(breakPointId, linePoints[1], keepValue, change.node.attributes));
    } else {
      changes.unshift(node.addFace(breakPointId, linePoints[0], keepValue, change.node.attributes));
      changes.unshift(node.addFace(breakPointId, keepValue, linePoints[1], change.node.attributes));
    }

    changes.unshift(node.addLineSegment(breakPointId, keepValue, change.node.attributes));
  }

  return changes;
};
